import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import moment from 'moment'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
} from '@mui/material'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import API from '../../../../services/API'
import { GlobalContext } from '../../../../App'
import BigModal from '../../../../components/Modals/BigModal'
import QRCodePopup from '../../../../components/QRCode/QRCodePopup'
import {
  SectionHeader,
  TabLabel,
} from '../../vehicles/VehicleComponents/SectionKit'
import { useMakeReadyTierAllowed } from '../../makeReadyModule/makeReadyAccess'
import AssignLoadListDialog from '../../makeReadyModule/AssignLoadListDialog'
import AssetMakeReadyBlock from '../../makeReadyModule/AssetMakeReadyBlock'
import MakeReadyHistoryAccordion from '../../makeReady/MakeReadyHistoryAccordion'
import AddNewAsset from '../AddNewAsset'
import AssetHero from './AssetHero'
import AssetOverview from './AssetOverview'
import DefectsSection from './Defects'
import ServicingSection from './Maintenance'
import AssetUnavailabilitySection, {
  isOverdueAssetPeriod,
} from './AssetUnavailabilitySection'
import AssetUnavailabilityDialog from './AssetUnavailabilityDialog'
import AttachmentsSection from './Attachments'
import NotesSection from './Comments'
import RecordMaintenance from './RecordMaintenance'
import AssignToVehicleDialog from './AssignToVehicle'
import AssignToStoreDialog from './AssignToStore'
import ConfirmDeleteDialog from './ConfirmDelete'
import { useDocumentTitle } from '../../../../helpers/DocumentTitleManager'

export const AssetContext = createContext()

const SingleAsset = () => {
  const { assetID } = useParams()
  const navigate = useNavigate()
  const { setToast } = useContext(GlobalContext)
  const makeReadyAllowed = useMakeReadyTierAllowed(2)
  const {setPageTitle} = useDocumentTitle()

  const [loading, setLoading] = useState(true)
  const [asset, setAsset] = useState(null)
  const [notFound, setNotFound] = useState(false)
  const [fetchError, setFetchError] = useState(false)
  const [maintenanceLogs, setMaintenanceLogs] = useState([])
  const [maintenanceLoading, setMaintenanceLoading] = useState(false)
  const [makeReadyStatus, setMakeReadyStatus] = useState(null)
  const [unavailability, setUnavailability] = useState([])
  const [unavailabilityLoading, setUnavailabilityLoading] = useState(false)
  const [unavailabilityError, setUnavailabilityError] = useState(false)
  // Which popup is open: edit | maintenance | assignVehicle | assignLoadList
  // | qr | delete. All are mounted once here and opened from the hero or
  // the tabs through the context.
  const [dialog, setDialog] = useState(null)
  const [tab, setTab] = useState('overview')

  // `quiet` refreshes in place after an edit rather than flashing the
  // whole page back to a progress bar.
  const loadAsset = ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true)
    setFetchError(false)
    API.get('/asset/' + assetID)
      .then((res) => {
        if (res.status === 200 && res.data?.asset) {
          setAsset(res.data.asset)
          setNotFound(false)
        } else if (res.status === 404) {
          setNotFound(true)
        } else {
          setFetchError(true)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    setTab('overview')
    setDialog(null)
    loadAsset()
  }, [assetID])

  useEffect(() => {
    if (assetID) {
      setPageTitle(assetID)
    }
  }, [assetID, setPageTitle])

  const loadMaintenanceLogs = () => {
    if (!asset?._id || !asset.requiresServicing) {
      setMaintenanceLogs([])
      return
    }
    setMaintenanceLoading(true)
    API.get(`/maintenancelogs?assetID=${asset._id}`)
      .then((res) => res.status === 200 && setMaintenanceLogs(res.data ?? []))
      .catch(() => {})
      .finally(() => setMaintenanceLoading(false))
  }
  useEffect(loadMaintenanceLogs, [asset?._id, asset?.requiresServicing])

  // Fetched eagerly so the Planned Downtime tab badge is accurate on load.
  const loadUnavailability = () => {
    if (!asset?._id) {
      setUnavailability([])
      return
    }
    setUnavailabilityLoading(true)
    setUnavailabilityError(false)
    API.get(`/fleet/assets/${asset._id}/unavailability`)
      .then((res) => {
        if (res.status === 200) {
          setUnavailability(res.data || [])
        } else {
          setUnavailabilityError(true)
        }
      })
      .catch(() => setUnavailabilityError(true))
      .finally(() => setUnavailabilityLoading(false))
  }
  useEffect(loadUnavailability, [asset?._id])

  const loadMakeReadyStatus = () => {
    if (!asset?._id || !makeReadyAllowed) {
      setMakeReadyStatus(null)
      return
    }
    API.get(`/makeready/status/${asset._id}`)
      .then((res) => res.status === 200 && setMakeReadyStatus(res.data))
      .catch(() => {})
  }
  useEffect(loadMakeReadyStatus, [
    asset?._id,
    asset?.makeReadyLoadList?._id,
    makeReadyAllowed,
  ])

  const activeDefects = asset?.currentDefects?.length ?? 0
  const serviceOverdue =
    asset?.requiresServicing &&
    asset?.latestMaintenanceLog?.expiry &&
    moment(asset.latestMaintenanceLog.expiry).isBefore(moment().startOf('day'))
  const attachmentCount = (asset?.attachments ?? []).filter((a) => a?.key)
    .length

  const tabs = useMemo(
    () =>
      [
        { key: 'overview', label: 'Overview' },
        {
          key: 'defects',
          label: 'Defects',
          count: activeDefects,
          color: activeDefects > 0 ? 'error' : 'default',
        },
        asset?.requiresServicing && {
          key: 'servicing',
          label: 'Servicing',
          count: serviceOverdue ? 'Overdue' : undefined,
          color: 'error',
        },
        // Downtime isn't gated on requiresServicing — any asset can be taken
        // out of use (e.g. a bag in quarantine).
        {
          key: 'downtime',
          label: 'Planned Downtime',
          count: unavailability.length || undefined,
          color: unavailability.some(isOverdueAssetPeriod)
            ? 'error'
            : 'warning',
        },
        { key: 'makeready', label: 'Make Ready' },
        {
          key: 'attachments',
          label: 'Attachments',
          count: attachmentCount || undefined,
        },
        { key: 'notes', label: 'Notes' },
      ].filter(Boolean),
    [
      asset?.requiresServicing,
      activeDefects,
      serviceOverdue,
      attachmentCount,
      unavailability,
    ],
  )

  const closeDialog = () => setDialog(null)

  const backButton = (
    <Box>
      <Button
        size="small"
        color="inherit"
        startIcon={<ArrowBackRoundedIcon />}
        onClick={() => navigate('/asset-manager')}
        sx={{ color: 'text.secondary' }}
      >
        Asset Manager
      </Button>
    </Box>
  )

  if (loading) return <LinearProgress />

  if (fetchError) {
    return (
      <Stack spacing={2}>
        {backButton}
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => loadAsset()}>
              Retry
            </Button>
          }
        >
          Unable to load this asset. Please try again later.
        </Alert>
      </Stack>
    )
  }

  if (notFound || !asset) {
    return (
      <Stack spacing={2}>
        {backButton}
        <Alert severity="warning">
          No asset with the ID <strong>{assetID}</strong> was found. It may
          have been deleted or re-tagged.
        </Alert>
      </Stack>
    )
  }

  return (
    <AssetContext.Provider
      value={{
        asset,
        setAsset,
        loadAsset: () => loadAsset({ quiet: true }),
        maintenanceLogs,
        maintenanceLoading,
        reloadMaintenance: loadMaintenanceLogs,
        makeReadyStatus,
        unavailability,
        openDialog: setDialog,
        closeDialog,
        openTab: setTab,
      }}
    >
      <Stack spacing={2}>
        {backButton}
        <AssetHero />
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Tabs
            value={tab}
            onChange={(e, value) => setTab(value)}
            variant="scrollable"
            allowScrollButtonsMobile
            sx={{
              px: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
              // A disabled scroll arrow keeps its 40px slot by default, which
              // left a gap before the first tab — collapse it entirely.
              '& .MuiTabs-scrollButtons.Mui-disabled': { display: 'none' },
            }}
          >
            {tabs.map((tabDef) => (
              <Tab
                key={tabDef.key}
                value={tabDef.key}
                label={
                  <TabLabel
                    label={tabDef.label}
                    count={tabDef.count}
                    color={tabDef.color}
                  />
                }
              />
            ))}
          </Tabs>
          <Box p={2}>
            {tab === 'overview' && <AssetOverview />}
            {tab === 'defects' && <DefectsSection />}
            {tab === 'servicing' && <ServicingSection />}
            {tab === 'downtime' && (
              <AssetUnavailabilitySection
                periods={unavailability}
                loading={unavailabilityLoading}
                error={unavailabilityError}
                onChanged={loadUnavailability}
                onAdd={() => setDialog('downtime')}
              />
            )}
            {tab === 'makeready' && (
              <Grid container spacing={2}>
                {makeReadyAllowed && (
                  <Grid item xs={12} md={4}>
                    <AssetMakeReadyBlock
                      assetId={asset._id}
                      onChanged={(loadList) =>
                        setAsset((prev) => ({
                          ...prev,
                          makeReadyLoadList: loadList,
                        }))
                      }
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={makeReadyAllowed ? 8 : 12}>
                  <Stack spacing={1.5}>
                    <SectionHeader
                      title="Check History"
                      caption="Every make ready check recorded against this asset"
                    />
                    <MakeReadyHistoryAccordion
                      assetOrVehicleId={asset._id}
                      plain
                    />
                  </Stack>
                </Grid>
              </Grid>
            )}
            {tab === 'attachments' && <AttachmentsSection />}
            {tab === 'notes' && <NotesSection />}
          </Box>
        </Paper>
      </Stack>

      <BigModal open={dialog === 'edit'} onClose={closeDialog}>
        <AddNewAsset
          existingAsset={asset}
          onClose={() => {
            closeDialog()
            loadAsset({ quiet: true })
            setToast({ message: 'Asset updated' })
          }}
        />
      </BigModal>
      <RecordMaintenance
        open={dialog === 'maintenance'}
        onClose={closeDialog}
        onSaved={() => {
          loadAsset({ quiet: true })
          loadMaintenanceLogs()
        }}
      />
      <AssetUnavailabilityDialog
        open={dialog === 'downtime'}
        assetID={asset._id}
        period={null}
        onClose={closeDialog}
        onSaved={loadUnavailability}
      />
      <AssignToVehicleDialog
        open={dialog === 'assignVehicle'}
        onClose={closeDialog}
      />
      <AssignToStoreDialog
        open={dialog === 'assignStore'}
        onClose={closeDialog}
      />
      <AssignLoadListDialog
        open={dialog === 'assignLoadList'}
        onClose={closeDialog}
        targetId={asset._id}
        kind="asset"
        targetLabel={asset.assetID}
        onAssigned={() => loadAsset({ quiet: true })}
      />
      <QRCodePopup
        open={dialog === 'qr'}
        onClose={closeDialog}
        url={`${window.location.origin}/asset-manager/${asset.assetID}`}
        title={`Asset ${asset.assetID}`}
        imageLabel={asset.assetID}
        scanHint="Scan to open this asset record"
      />
      <ConfirmDeleteDialog open={dialog === 'delete'} onClose={closeDialog} />
    </AssetContext.Provider>
  )
}

export default SingleAsset
